import { type PaletteKey, type Pixel, PixelCanvas, sphere, sprite } from '../../lib';
import { gunOutline } from './shared';

/**
 * Effect skins' guns (ADR-0113): the default gun (the Pollinator) redrawn whole for a skin, at 4× density with the
 * Pollinator's frame, origin, grip and muzzle (64×36, origin and fist at [16,20], the muzzle at x 64 on the origin row),
 * so the client swaps its frames and nothing else. Each front narrows to its muzzle, like the Pollinator's: it points
 * where it shoots. Drawn as fills inside the guns' sel-out (`gunOutline` twice, 2 art px), and kept clear of skin and
 * hair tones behind the grip (shared.ts: it meets the gardener's face).
 */

const W = 64;
const H = 36;

/**
 * Fills a box x0..x1, y0..y1 (inclusive) whose corners are cut by `ins` (the inset of its first rows, outermost first:
 * [2, 1] rounds a corner over two rows), on the `sides` given ('l' left, 'r' right), each pixel toned by `tone(x, y)`.
 */
export function box(
  c: PixelCanvas,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  ins: readonly number[],
  tone: (x: number, y: number) => Pixel,
  sides = 'lr',
): void {
  for (let y = y0; y <= y1; y++) {
    const k = Math.min(y - y0, y1 - y);
    const inset = ins[k] ?? 0;
    const a = sides.includes('l') ? x0 + inset : x0;
    const b = sides.includes('r') ? x1 - inset : x1;
    for (let x = a; x <= b; x++) {
      const col = tone(x, y);
      if (col !== null) c.set(x, y, col);
    }
  }
}

/** A tone by row for a lit slab of `y0`..`y1`: `lit` rows on top, then mid, `shade` 2 rows over `deep` ones. */
export const byRow =
  (y0: number, y1: number, [deep, shade, mid, lit]: readonly PaletteKey[], litRows = 1, deepRows = 1) =>
  (_x: number, y: number): PaletteKey =>
    (y - y0 < litRows ? lit : y1 - y < deepRows ? deep : y1 - y < deepRows + 2 ? shade : mid) as PaletteKey;

/** Sets each [x, y] of `pts` to `col`. */
export const dots = (c: PixelCanvas, col: PaletteKey, pts: readonly (readonly [number, number])[]): PixelCanvas => {
  for (const [x, y] of pts) c.set(x, y, col);
  return c;
};

/**
 * A round part along columns x0..x1 about row `cy`, its half-height going from h0 to h1 (narrowing to the muzzle: the
 * fronts point where they shoot), each pixel toned by `tone(top, bottom, x, y)`: its rows from its column's top and
 * bottom. Rows round half up, so its top edge steps a column ahead of its bottom one.
 */
export function taper(
  c: PixelCanvas,
  x0: number,
  x1: number,
  cy: number,
  h0: number,
  h1: number,
  tone: (top: number, bottom: number, x: number, y: number) => Pixel,
): void {
  for (let x = x0; x <= x1; x++) {
    const h = h0 + ((h1 - h0) * (x - x0)) / Math.max(1, x1 - x0);
    const [a, b] = [Math.round(cy - h), Math.round(cy + h)];
    for (let y = a; y <= b; y++) {
      const col = tone(y - a, b - y, x, y);
      if (col !== null) c.set(x, y, col);
    }
  }
}

/** A round part's tone by its rows from its top and bottom: `lit` on top, `deep` at the bottom, `shade` over it. */
export const round =
  ([deep, shade, mid, lit]: readonly PaletteKey[]) =>
  (top: number, bottom: number): PaletteKey =>
    (top === 0 ? lit : bottom === 0 ? deep : bottom === 1 && top > 1 ? shade : mid) as PaletteKey;

/** The fill wrapped in the guns' 2-px sel-out (shared.ts `gun4` for a canvas drawn in code). */
export const outlined = (draw: (c: PixelCanvas) => void) => (c: PixelCanvas) => {
  draw(c);
  gunOutline(gunOutline(c));
};

// --- Bubble gun ---------------------------------------------------------------------------------------------------

/** Bubbles rising in the tank, per frame: [x, y, r] (1: a speck, 2: a ring). */
const RISING = [
  [
    [26, 11, 2],
    [31, 9, 1],
    [35, 11, 2],
    [40, 8, 1],
  ],
  [
    [26, 9, 2],
    [31, 11, 1],
    [35, 9, 2],
    [40, 11, 1],
  ],
  [
    [26, 11, 1],
    [31, 9, 2],
    [35, 11, 1],
    [40, 9, 2],
  ],
] as const;

const bubbleGun = (frame: number) =>
  outlined((c) => {
    // The stock: a short rounded pump in candy pink, a yellow cap at its end.
    box(c, 5, 17, 22, 26, [3, 1], byRow(17, 26, ['P', 'p', 'p', 'i'], 1, 1), 'l');
    box(c, 5, 18, 7, 25, [1], (_x, y) => (y < 20 ? 'y' : y > 23 ? 'U' : 'Y'), 'l');
    // The body: a chunky blaster, lit pink on top, a white glint along it, purple underneath.
    box(c, 22, 14, 46, 26, [2, 1], byRow(14, 26, ['P', 'p', 'p', 'i'], 2, 2), 'r');
    c.hline(24, 43, 15, 'w');
    // A big yellow button on its side, and a heart.
    sphere(c, 40.5, 20.5, 2.6, 2.6, ['U', 'Y', 'y'], { bands: [-0.1, 0.55] });
    dots(c, 'i', [
      [28, 19],
      [30, 19],
      [27, 20],
      [28, 20],
      [29, 20],
      [30, 20],
      [31, 20],
      [28, 21],
      [29, 21],
      [30, 21],
      [29, 22],
    ]);
    dots(c, 'w', [[27, 19]]);
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? 'Y' : 'U'), 'r');
    // The soap tank lying on top: a clear capsule (pale glass, lit cyan rim top-left, blue rim under), soap in its lower
    // half (a bright surface line), bubbles rising in it; a yellow filler cap on its top.
    box(c, 38, 2, 42, 3, [1], (_x, y) => (y === 2 ? 'y' : 'Y'));
    box(c, 21, 4, 45, 13, [3, 1, 1], (x, y) => {
      const rim = y === 4 || y === 13 || x === 21 || x === 45 || ((y === 5 || y === 12) && (x < 24 || x > 42));
      if (rim) return y < 9 && x < 40 ? 'c' : 'B';
      if (y >= 8) return y === 8 ? 'c' : y > 11 ? 'B' : 'b';
      return '5';
    });
    // The tank's strap onto the body.
    box(c, 25, 13, 27, 14, [], () => 'Y');
    box(c, 39, 13, 41, 14, [], () => 'Y');
    // Its glint: a long white streak on the glass, a short one under it.
    c.hline(24, 36, 5, 'w').hline(23, 26, 6, 'w');
    for (const [x, y, r] of RISING[frame] ?? RISING[0]) {
      if (r === 1) c.set(x, y, 'w');
      else
        dots(c, 'w', [
          [x, y - 1],
          [x - 1, y],
        ])
          .set(x + 1, y, 'c')
          .set(x, y + 1, 'c');
    }
    // The nozzle: a yellow cone stepping down out of the body into a thin stem.
    const yellow = round(['U', 'U', 'Y', 'y']);
    taper(c, 47, 51, 20, 4, 2, yellow);
    taper(c, 52, 54, 20, 1, 1, yellow);
    // The wand: a small yellow ring (narrower than the body), a soap film across it (cyan, a glint).
    for (let y = 15; y <= 25; y++)
      for (let x = 52; x <= 61; x++) {
        const d = Math.hypot(x + 0.5 - 57.5, y + 0.5 - 20.5);
        if (d > 4.4) continue;
        if (d > 2.4) c.set(x, y, x + y < 75 ? 'y' : x + y > 79 ? 'U' : 'Y');
        else c.set(x, y, x + y < 76 ? 'w' : 'c');
      }
  });

// --- Angel wings --------------------------------------------------------------------------------------------------

type Pt = readonly [number, number];

/**
 * A wing's outline round its root (0, 0), art px, x back (−) and y up (−): its leading edge rising and arching back to
 * the tip, then the trailing edge scalloped by three notches between the flight feathers, back to the root.
 */
const WING: readonly Pt[] = [
  [0, 0],
  [1, -4],
  [0.5, -8],
  [-2.5, -11],
  [-6.5, -13],
  [-11, -13.5],
  [-12.5, -12.2],
  [-10, -10.5],
  [-12.8, -9],
  [-12.2, -7.4],
  [-9.5, -6.6],
  [-11.6, -5],
  [-10.8, -3.4],
  [-7.6, -3],
  [-8.6, -1.4],
  [-6.6, 0.2],
  [-3, 1.2],
];
/** The notches' feather lines: from each notch towards the root. */
const QUILLS: readonly (readonly [Pt, Pt])[] = [
  [
    [-10, -10.5],
    [-3.5, -7],
  ],
  [
    [-9.5, -6.6],
    [-3, -4.2],
  ],
  [
    [-7.6, -3],
    [-2.2, -1.8],
  ],
];

/** Whether pixel (x, y)'s centre is inside polygon `pts`. */
function inside(pts: readonly Pt[], x: number, y: number): boolean {
  const [px, py] = [x + 0.5, y + 0.5];
  let into = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i] as Pt;
    const [xj, yj] = pts[j] as Pt;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) into = !into;
  }
  return into;
}

/**
 * A white feathered wing rooted at (rx, ry), turned by `turn` (radians, + raises it) and scaled by `k` (and `ky` up and
 * down): lit along its top and back edges, shaded along its foot, a quill line in each notch, gold along its root. The
 * far wing is a step darker.
 */
function wing(c: PixelCanvas, rx: number, ry: number, turn: number, k: number, ky: number, far: boolean): void {
  const [ca, sa] = [Math.cos(turn), Math.sin(turn)];
  const at = ([x, y]: Pt): Pt => [rx + (x * ca - y * sa) * k, ry + (x * sa + y * ca) * k * ky];
  const pts = WING.map(at);
  const [lit, face, shade]: readonly PaletteKey[] = far ? ['5', '4', '3'] : ['w', '5', '4'];
  const mine = new PixelCanvas(c.w, c.h);
  for (let y = 0; y < c.h; y++) for (let x = 0; x < c.w; x++) if (inside(pts, x, y)) mine.set(x, y, 'w');
  // Edges: lit where the outside is above or behind, shaded where it is below.
  const out = (x: number, y: number) => mine.get(x, y) === null;
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++) {
      if (out(x, y)) continue;
      if (out(x, y - 1) || out(x - 1, y)) c.set(x, y, lit as PaletteKey);
      else if (out(x, y + 1) || out(x + 1, y)) c.set(x, y, shade as PaletteKey);
      else c.set(x, y, face as PaletteKey);
    }
  for (const [a, b] of QUILLS) {
    const [p, q] = [at(a), at(b)];
    c.line(p[0], p[1], q[0], q[1], shade as PaletteKey);
  }
  // Gold along its root (the near wing's).
  if (!far) {
    const [p, q] = [at([-6, 0]), at([0.5, -0.5])];
    c.line(p[0], p[1], q[0], q[1], 'Y');
  }
}

/** The wings per frame: [turn, scale, vertical scale] (rest, up, down, coming back). */
const FLAP = [
  [0, 1, 1],
  [0.5, 0.9, 1],
  [-0.6, 1.02, 0.6],
  [-0.22, 1, 0.88],
] as const;

const angelGun = (frame: number) =>
  outlined((c) => {
    const [turn, k, ky] = FLAP[frame] ?? FLAP[0];
    // The far wing first, peeking out over and behind the near one.
    wing(c, 45, 15.5, turn + 0.25, k * 0.9, ky, true);
    // The stock: slim white porcelain, a gold cap.
    box(c, 6, 16, 22, 24, [2, 1], byRow(16, 24, ['4', '4', '5', 'w'], 2, 1), 'l');
    box(c, 6, 17, 8, 23, [1], (_x, y) => (y < 19 ? 'y' : y > 21 ? 'U' : 'Y'), 'l');
    // The body: white, gold trim along its top, a gold scroll on its side.
    box(c, 22, 14, 45, 25, [2, 1], byRow(14, 25, ['4', '4', '5', 'w'], 2, 2), 'r');
    c.hline(23, 44, 14, 'Y');
    dots(c, 'Y', [
      [29, 19],
      [30, 18],
      [31, 18],
      [32, 19],
      [32, 20],
      [33, 21],
      [34, 21],
      [35, 20],
      [35, 19],
      [36, 18],
      [37, 18],
      [38, 19],
    ]);
    // The trigger, gold.
    box(c, 31, 26, 34, 28, [0, 1], (_x, y) => (y === 26 ? 'Y' : 'U'), 'r');
    // A herald's trumpet: a gold collar out of the body, a long thin gold pipe with a knob along it, a small bell at
    // its very end (no taller than the collar), its rim lit.
    const gold = round(['U', 'U', 'Y', 'y']);
    box(c, 46, 17, 47, 22, [], byRow(17, 22, ['U', 'U', 'Y', 'y'], 1, 1));
    taper(c, 48, 56, 19.5, 0.9, 0.9, gold);
    taper(c, 51, 52, 19.5, 1.4, 1.4, gold);
    for (let x = 56; x <= 61; x++) {
      const h = 0.9 + ((x - 55) / 6) ** 2 * 2;
      taper(c, x, x, 19.5, h, h, (top, bottom) => (x === 61 ? (top < bottom ? 'y' : 'Y') : gold(top, bottom)));
    }
    // The near wing, over the body's back.
    wing(c, 41, 16.5, turn, k, ky, false);
  });

// --- Plasma gun ---------------------------------------------------------------------------------------------------

/** The coil's steel turns (x of each). */
const TURNS = [34, 37, 40, 43, 46, 49] as const;

const plasmaGun = (frame: number) =>
  outlined((c) => {
    // The stock: dark gunmetal, a violet stripe, a fin.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, ['1', '2', '3', '4'], 1, 2), 'l');
    c.hline(8, 21, 19, 'P').hline(8, 21, 20, 'p');
    // The body: gunmetal, lit on top, a fin on its back, a violet light.
    box(c, 22, 13, 33, 26, [2, 1], byRow(13, 26, ['1', '2', '3', '4'], 2, 3));
    box(c, 23, 9, 29, 12, [], (x, y) => (y - 9 < (x - 23) * 0.6 ? null : y === 12 ? '3' : x === 29 ? '2' : '4'));
    dots(c, 'i', [[26, 17]]);
    dots(c, 'p', [
      [27, 17],
      [26, 18],
    ]);
    dots(c, 'P', [[27, 18]]);
    // The trigger.
    box(c, 28, 27, 31, 29, [0, 1], (_x, y) => (y === 27 ? '3' : '2'), 'r');
    // The coil: a glass tube glowing cyan, wound with dark steel; a bright band runs along it.
    const hot = 34 + ((frame * 5) % 20);
    box(c, 33, 15, 52, 24, [], (x, y) => {
      const near = Math.abs(x - hot);
      if (y === 15) return '3';
      if (y === 24) return '1';
      if (y < 18) return 'w';
      if (y > 21) return near < 4 ? 'c' : 'b';
      return near < 3 ? 'w' : 'c';
    });
    for (const t of TURNS) box(c, t, 14, t + 1, 25, [1], (x) => (x === t ? '4' : '2'));
    // Rails over and under it.
    box(c, 31, 12, 52, 13, [], (_x, y) => (y === 12 ? '4' : '2'), 'r');
    box(c, 31, 26, 50, 27, [], (_x, y) => (y === 26 ? '2' : '1'), 'r');
    // The emitter: a gunmetal cone the coil feeds into, a slit of plasma along it, narrowing to a glowing prong.
    taper(c, 51, 58, 19.5, 7, 0.9, (top, bottom) =>
      top === 0 ? '4' : bottom === 0 ? '1' : bottom === 1 ? '2' : top < 3 ? '4' : '3',
    );
    for (let x = 51; x <= 61; x++) {
      const near = Math.abs(x - (51 + ((frame * 3) % 11)));
      c.set(x, 19, near < 2 || x > 59 ? 'w' : 'c').set(x, 20, near < 2 ? 'c' : 'b');
    }
    dots(c, frame % 2 ? 'c' : 'w', [[61, 20]]);
  });

// --- Garden hose --------------------------------------------------------------------------------------------------

/** The hose's middle line (art px), from the nozzle's back end down and off the frame's corner. */
const HOSE = [
  [14, 19.5],
  [9, 20.5],
  [5.5, 23.5],
  [4, 28],
  [5, 32],
  [1, 34.5],
] as const;

const hoseGun = () =>
  outlined((c) => {
    // The hose (green, lit along its top, darker underneath) curling off the back of the nozzle.
    for (let i = 0; i + 1 < HOSE.length; i++) {
      const [ax, ay] = HOSE[i] as readonly [number, number];
      const [bx, by] = HOSE[i + 1] as readonly [number, number];
      const n = Math.ceil(Math.hypot(bx - ax, by - ay) * 3);
      const [nx, ny] = [-(by - ay), bx - ax].map((v) => v / Math.hypot(bx - ax, by - ay)) as [number, number];
      // The normal towards the light (up-left).
      const k = nx * -0.6 + ny * -0.8 > 0 ? 1 : -1;
      for (let s = 0; s <= n; s++) {
        const x = ax + ((bx - ax) * s) / n;
        const y = ay + ((by - ay) * s) / n;
        for (let o = -2.6; o <= 2.6; o += 0.5) {
          const px = Math.round(x + nx * o * k);
          const py = Math.round(y + ny * o * k);
          c.set(px, py, o > 1.4 ? 'l' : o < -1.4 ? 'G' : 'g');
        }
      }
    }
    // The body: a brass nozzle, lit along its top, knurled bands for the grip, a brass coupling for the hose.
    box(c, 13, 15, 42, 23, [2, 1], byRow(15, 23, ['n', 'U', 'Y', 'y'], 2, 2), 'l');
    c.hline(16, 40, 16, 'e');
    box(c, 11, 16, 14, 22, [1], byRow(16, 22, ['n', 'U', 'Y', 'y'], 1, 1), 'l');
    // The handle under it, behind the fist.
    box(c, 18, 24, 27, 29, [0, 1], byRow(24, 29, ['n', 'U', 'Y', 'Y'], 1, 1));
    // The lever in front of the fist: red, pressed to the body.
    box(c, 30, 24, 38, 27, [0, 1], (_x, y) => (y === 24 ? 'r' : y > 25 ? 'R' : 'r'), 'r');
    // The knurled collar, then a long brass cone narrowing to its tip, a glint along its top, a small hole at its end.
    box(c, 42, 16, 48, 22, [], byRow(16, 22, ['n', 'U', 'Y', 'y'], 1, 2));
    for (const x of [44, 46, 48]) c.vline(x, 17, 21, 'U');
    const brass = round(['n', 'U', 'Y', 'y']);
    taper(c, 49, 61, 19, 3, 1, (top, bottom, x) => (top === 1 && x < 57 ? 'e' : brass(top, bottom)));
    dots(c, 'n', [[61, 19]]);
    // Wet at the tip: a drop hanging under it.
    dots(c, 'c', [[60, 21]]).set(60, 22, 'b');
  });

export const geometry = {
  group: 'actor',
  size: [W, H],
  origin: [16, 20],
  hand: [16, 20],
  density: 4,
  flash: ['idle'],
} as const;

export default [
  sprite({
    name: 'pollinator_bubble',
    ...geometry,
    notes: `Bubble gun (ADR-0113): a toy blaster in candy pink with a clear soap tank lying on top (soap in its lower half,
bubbles rising in it, a yellow filler cap), a yellow nozzle stepping down from the body (a cone, then a thin stem) to
a small bubble wand ring at the muzzle, a soap film across it: the ring (9 rows) stays narrower than the body (13), so
the front points rather than flares. Rubric (4×): S5 R5 P5 L4 C4 A4 F4. Weakest: the heart and the button read as two
dots at 1:1; the soap is 4 rows.`,
    anims: { idle: { fps: 4, frames: [bubbleGun(0), bubbleGun(1), bubbleGun(2)] } },
  }),
  sprite({
    name: 'pollinator_angel',
    ...geometry,
    notes: `Angel wings (ADR-0113): white porcelain and gold, a slim herald's trumpet out of its front (a gold collar, a
2-row pipe with a knob, a small bell at its very end, no taller than the collar), a feathered wing on each side (the far
one behind), which flap on each shot (fire: up, down, coming back, rest). The wing is an outline polygon (\`WING\`)
turned and squashed per frame, lit along its top and back, a quill in each notch; it stays over the body (x ≥ 22:
behind the grip it would meet the face). Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the wing is 13 px at 1:1, its
feathers read from ×4 up; the body is a plain white tube.`,
    anims: {
      idle: { frames: [angelGun(0)] },
      fire: { fps: 20, loop: false, frames: [angelGun(1), angelGun(2), angelGun(3), angelGun(0)] },
    },
  }),
  sprite({
    name: 'pollinator_plasma',
    ...geometry,
    notes: `Plasma gun (ADR-0113): gunmetal with a violet stripe and a fin, a glass coil glowing cyan wound with steel (a
bright band running along it), rails over and under it, an emitter: a gunmetal cone the coil feeds into, a slit of
plasma along it (the bright band runs on into it), narrowing to a glowing 2-row prong, its tip flickering. Rubric (4×):
S5 R5 P5 L4 C4 A4 F4. Weakest: the fin reads as a notch; the violet light is 2 px.`,
    anims: { idle: { fps: 8, frames: [plasmaGun(0), plasmaGun(1), plasmaGun(2), plasmaGun(3)] } },
  }),
  sprite({
    name: 'pollinator_hose',
    ...geometry,
    notes: `Garden hose (ADR-0113): a brass pistol nozzle (a red lever, a knurled collar, then a long brass cone narrowing
to a small hole at its tip, a drop hanging under it: a real brass nozzle narrows, never flares) on a short green hose
curling off its back end and out of the frame (green, never a skin or hair tone, and under the stock line: it meets the
face). Rubric (4×): S4 R5 P5 L4 C4 A- F4. Weakest: the hose reads as a tail at 1:1; the fist hides the lever.`,
    anims: { idle: { frames: [hoseGun()] } },
  }),
];
